import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, MailQuestion } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ModuleTitleLIFT } from "@/components/lift/ModuleTitleLIFT";

export default function PeerTasksInboxPage() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/lift/peer-tasks");
      setTasks(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-6" data-testid="lift-peer-tasks-inbox">
      <Link to="/lift" className="patria-btn-ghost -ml-3">
        <ChevronLeft className="h-4 w-4" strokeWidth={1.5} /> LIFT home
      </Link>
      <div>
        <ModuleTitleLIFT />
        <h1 className="mt-3 text-4xl font-serif text-forest-900">Feedback Requests For Me</h1>
        <p className="mt-2 max-w-2xl text-charcoal-soft">
          A short reflection on each teammate — your voice shows up in aggregate, never with your name.
        </p>
      </div>

      {loading && <p className="italic text-charcoal-muted">Loading…</p>}
      {!loading && tasks.length === 0 && (
        <div className="patria-card p-10 text-center">
          <MailQuestion className="h-8 w-8 mx-auto text-forest-600" strokeWidth={1.5} />
          <p className="mt-3 text-charcoal-soft">You have no peer feedback tasks right now.</p>
        </div>
      )}

      <ul className="space-y-3">
        {tasks.map((t) => (
          <li key={t.id} className="patria-card p-5" data-testid={`peer-task-${t.id}`}>
            <div className="flex items-center gap-4">
              <span
                className={
                  t.response_status === "submitted"
                    ? "patria-chip-green"
                    : t.status === "declined"
                    ? "patria-chip-terra"
                    : "patria-chip-neutral"
                }
              >
                {t.response_status || t.status}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-forest-900 truncate">{t.subject_name}</p>
                <p className="text-xs text-charcoal-muted truncate">
                  {t.cycle_name} · {t.subject_title || "—"} · {t.subject_department || "—"}
                  {t.due_date ? ` · due ${t.due_date}` : ""}
                </p>
              </div>
              <button
                className="patria-btn-primary"
                onClick={() => navigate(`/lift/peer-tasks/${t.id}`)}
                data-testid={`peer-task-open-${t.id}`}
              >
                {t.response_status === "submitted" ? "Review" : "Respond"}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
